import React, {FC} from 'react';
import {connect} from 'react-redux';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {StartGuideTaskTypesEnum} from '../../../../models/api/preferencesModel';
import {SettingsRootPagesEnum} from '../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../models/url/settingsUrlHelpers';
import {updateStartGuideTasksAction} from '../../../../store/data/preferences/preferencesActions';
import {getCompletedStartGuideTasks} from '../../../../store/data/preferences/preferencesSelectors';
import {getHasOnboardingExperimentEarlyWorkflowIntro} from '../../../../store/featureFlags/featureFlagsSelectors';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {getHasGlobalAdminStartGuide} from '../../../../store/stateless/startGuideSelectors';
import {totalTaskCount, useTaskCompletionProgress} from '../../../onboarding/admin/adminOnboardingHelpers';
import {AdminSetupBanner} from './adminSetupBanner';

/*
 * Props.
 */

interface AdminSetupBannerContainerComponentProps {
  hasGlobalAdminStartGuide: boolean;
  hasOnboardingExperimentEarlyWorkflowIntro: boolean;
  shouldHideAdminSetupBanner: boolean;
  navigateToAdminHome: Handler;
  updateStartGuideTasks: HandlerOf<StartGuideTaskTypesEnum>;
}

/*
 * Component.
 */

export const AdminSetupBannerContainerComponent: FC<AdminSetupBannerContainerComponentProps> = (props) => {
  const {
    hasGlobalAdminStartGuide,
    hasOnboardingExperimentEarlyWorkflowIntro,
    shouldHideAdminSetupBanner,
    navigateToAdminHome,
    updateStartGuideTasks,
  } = props;

  const {progress, shouldAddTeamInbox, shouldAddTeammate, shouldExploreRules} = useTaskCompletionProgress();

  const isAnyTaskComplete = progress >= Math.floor((1 / totalTaskCount) * 100);

  const shouldShowAdminSetupBanner =
    !shouldHideAdminSetupBanner &&
    hasOnboardingExperimentEarlyWorkflowIntro &&
    hasGlobalAdminStartGuide &&
    isAnyTaskComplete;

  if (!shouldShowAdminSetupBanner) {
    return null;
  }

  // Refresh the banner state when the progress changes.
  const bannerKey = `isComplete:${progress}`;

  return (
    <AdminSetupBanner
      key={bannerKey}
      progress={progress}
      shouldAddTeamInbox={shouldAddTeamInbox}
      shouldAddTeammate={shouldAddTeammate}
      shouldExploreRules={shouldExploreRules}
      navigateToAdminHome={navigateToAdminHome}
      updateStartGuideTasks={updateStartGuideTasks}
    />
  );
};

/*
 * Selectors.
 */

// Hide the banner permanently if the user dismissed the banner when setup progress was complete.
const getShouldHideAdminSetupBanner = createSelector(
  [getCompletedStartGuideTasks],
  (completedStartGuideTasks) =>
    completedStartGuideTasks.includes(StartGuideTaskTypesEnum.HELP_CENTER_SUCCESS_DISMISS),
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  hasGlobalAdminStartGuide: getHasGlobalAdminStartGuide,
  hasOnboardingExperimentEarlyWorkflowIntro: getHasOnboardingExperimentEarlyWorkflowIntro,
  shouldHideAdminSetupBanner: getShouldHideAdminSetupBanner,
}));

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  navigateToAdminHome: () =>
    navigateToLinkAction(buildSettingsPath({rootPage: SettingsRootPagesEnum.ADMIN_HOME})),
  updateStartGuideTasks: updateStartGuideTasksAction,
}));

export const AdminSetupBannerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AdminSetupBannerContainerComponent);
